import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace("#", ""));
    const session_id = params.get("session_id");
    if (!session_id) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        if (data.session_token) {
          localStorage.setItem("bos_access_token", data.session_token);
        }
        if (data.workspace?.workspace_id) {
          localStorage.setItem("bos_workspace_id", data.workspace.workspace_id);
        }
        const me = await refresh();
        if (!me?.user) {
          setError("Sign-in accepted, but your account couldn't be loaded. Please try email + password, or contact support.");
          return;
        }
        // Strip hash
        window.history.replaceState({}, "", "/app");
        navigate("/app", { replace: true });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (detail?.message || e?.message || "Unknown error");
        setError(`${msg}${status ? ` (${status})` : ""}`);
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-red-500/10 text-red-500 grid place-items-center text-2xl">!</div>
            <h1 className="mt-4 font-display text-2xl tracking-tight">Sign-in failed</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <div className="mt-6 flex gap-2 justify-center">
              <button onClick={() => navigate("/login", { replace: true })} className="px-4 h-10 rounded-md border border-border text-sm font-medium hover:bg-muted transition">Back to login</button>
              <button onClick={() => navigate("/signup", { replace: true })} className="px-4 h-10 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition">Create account</button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}
